import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { agentManagementService } from '../../services/agentManagementService';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import GeneralSettings from './components/GeneralSettings';
import AIModelConfiguration from './components/AIModelConfiguration';
import ConversationSettings from './components/ConversationSettings';
import FeatureToggles from './components/FeatureToggles';
import KnowledgeBase from './components/KnowledgeBase';
import StyleSettings from './components/StyleSettings';
import Button from '../../components/ui/Button';

const AgentConfiguration = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const agentId = searchParams?.get('agent');
  const isEditing = !!agentId;
  
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Agent configuration state - aligned with database schema
  const [agentConfig, setAgentConfig] = useState({
    // Database fields from agents table
    name: '',
    display_name: '',
    system_prompt: '',
    tags: [],
    support_email: '',
    appearance: null,
    provider: 'openai',
    model: 'gpt-5',
    temperature: 0.67,
    api_key: '',
    initial_messages: [],
    conversation_starters: [],
    persistent_conversation: false,
    streaming_enabled: true,
    audio_enabled: false,
    vision_enabled: false,
    show_sources: true,
    debug_mode: false,
    kb_hits: 5
  });

  // Load existing agent data if editing
  useEffect(() => {
    const loadAgent = async () => {
      if (!isEditing || !user) return;

      try {
        setLoading(true);
        setError(null);
        
        const agent = await agentManagementService?.getAgentById(agentId);
        
        if (agent) {
          setAgentConfig({
            name: agent?.name || '',
            display_name: agent?.display_name || '',
            system_prompt: agent?.system_prompt || '',
            tags: agent?.tags || [],
            support_email: agent?.support_email || '',
            appearance: agent?.appearance || null,
            provider: agent?.provider || 'openai',
            model: agent?.model || 'gpt-5',
            temperature: agent?.temperature || 0.67,
            api_key: agent?.api_key || '',
            initial_messages: agent?.initial_messages || [],
            conversation_starters: agent?.conversation_starters || [],
            persistent_conversation: agent?.persistent_conversation || false,
            streaming_enabled: agent?.streaming_enabled ?? true,
            audio_enabled: agent?.audio_enabled || false,
            vision_enabled: agent?.vision_enabled || false,
            show_sources: agent?.show_sources ?? true,
            debug_mode: agent?.debug_mode || false,
            kb_hits: agent?.kb_hits || 5
          });
        }
      } catch (error) {
        setError(error?.message || 'Failed to load agent configuration. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading) {
      loadAgent();
    }
  }, [agentId, isEditing, user, authLoading]);

  // Handle configuration updates
  const updateConfig = (updates) => {
    setAgentConfig(prev => ({
      ...prev,
      ...updates
    }));
  };

  // Save agent configuration
  const handleSave = async () => {
    if (!user) {
      setError('You must be logged in to save agents.');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccessMessage('');

      // Validate required fields
      if (!agentConfig?.name?.trim()) {
        throw new Error('Agent name is required');
      }

      // Prepare data according to database schema
      const agentData = {
        name: agentConfig?.name?.trim(),
        display_name: agentConfig?.display_name?.trim() || null,
        system_prompt: agentConfig?.system_prompt?.trim() || null,
        tags: agentConfig?.tags || [],
        support_email: agentConfig?.support_email?.trim() || null,
        appearance: agentConfig?.appearance,
        provider: agentConfig?.provider,
        model: agentConfig?.model,
        temperature: agentConfig?.temperature,
        api_key: agentConfig?.api_key?.trim() || null,
        initial_messages: agentConfig?.initial_messages || [],
        conversation_starters: agentConfig?.conversation_starters || [],
        persistent_conversation: agentConfig?.persistent_conversation,
        streaming_enabled: agentConfig?.streaming_enabled,
        audio_enabled: agentConfig?.audio_enabled,
        vision_enabled: agentConfig?.vision_enabled,
        show_sources: agentConfig?.show_sources,
        debug_mode: agentConfig?.debug_mode,
        kb_hits: agentConfig?.kb_hits
      };

      let result;
      if (isEditing) {
        result = await agentManagementService?.updateAgent(agentId, agentData);
        setSuccessMessage('Agent updated successfully!');
      } else {
        result = await agentManagementService?.createAgent(agentData);
        setSuccessMessage('Agent created successfully!');
        // Navigate to edit mode with new agent ID
        setTimeout(() => {
          navigate(`/agent-configuration?agent=${result?.id}`, { replace: true });
        }, 1500);
      }

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      
    } catch (error) {
      setError(error?.message || 'Failed to save agent. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Handle cancel/back
  const handleCancel = () => {
    if (window?.confirm('Are you sure you want to leave? Any unsaved changes will be lost.')) {
      navigate('/ai-agents-dashboard');
    }
  };

  // Show loading state while auth is loading
  if (authLoading || (isEditing && loading)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">
            {authLoading ? 'Loading...' : 'Loading agent configuration...'}
          </p>
        </div>
      </div>
    );
  }

  // Show sign-in prompt if not authenticated
  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-[80vh]">
          <div className="text-center max-w-md mx-auto px-6">
            <h2 className="text-2xl font-semibold text-foreground mb-4">Sign In Required</h2>
            <p className="text-muted-foreground mb-6">
              Please sign in to configure AI agents.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="bg-primary text-primary-foreground px-6 py-2 rounded-md hover:bg-primary/90 transition-colors"
            >
              Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="flex">
        <Sidebar 
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
        
        <main className={`flex-1 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'} pb-16 lg:pb-0`}>
          <div className="p-6 max-w-4xl">
            {/* Page Header */}
            <div className="mb-6">
              <h1 className="text-2xl font-semibold text-foreground mb-2">
                {isEditing ? 'Agent bearbeiten' : 'Neuen Agent erstellen'}
              </h1>
              <p className="text-muted-foreground">
                {isEditing 
                  ? 'Bearbeiten Sie die Konfiguration Ihres AI-Chatbots' :'Konfigurieren Sie Ihren neuen AI-Chatbot'
                }
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md mb-6">
                <p className="text-sm">{error}</p>
                <button
                  onClick={() => setError(null)}
                  className="text-red-500 hover:text-red-700 text-xs underline mt-1"
                >
                  Dismiss
                </button>
              </div>
            )}

            {/* Success Message */}
            {successMessage && (
              <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-md mb-6">
                <p className="text-sm">{successMessage}</p>
              </div>
            )}

            <div className="space-y-8">
              {/* General Settings */}
              <GeneralSettings
                config={agentConfig}
                onUpdate={updateConfig}
              />

              {/* AI Model Configuration */}
              <AIModelConfiguration
                config={agentConfig}
                onUpdate={updateConfig}
              />

              {/* Conversation Settings */}
              <ConversationSettings
                config={agentConfig}
                onUpdate={updateConfig}
              />

              {/* Feature Toggles */}
              <FeatureToggles
                config={agentConfig}
                onUpdate={updateConfig}
              />

              {/* Knowledge Base */}
              <KnowledgeBase
                config={agentConfig}
                onUpdate={updateConfig}
              />

              {/* Style Settings */}
              <StyleSettings
                config={agentConfig}
                onUpdate={updateConfig}
              />

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-3 pt-6 border-t">
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Abbrechen
                </Button>
                <Button
                  variant="default"
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground"
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin mr-2" />
                      Speichern...
                    </>
                  ) : (
                    isEditing ? 'Änderungen speichern' : 'Agent erstellen'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AgentConfiguration;